import React, { useEffect, useState } from "react";
import { toast } from "../utils/toast";
import AdminWorkspaceNav from "../components/admin/AdminWorkspaceNav";
import {
  createAdminHomepageSection,
  deleteAdminHomepageSection,
  fetchAdminHomepageSections,
  updateAdminHomepageSection,
} from "../services/adminService";
import { HomepageSectionContent } from "../types/store";

const sectionLabels: Record<string, string> = {
  categories: "Category section",
  "hard-to-find": "Hard to find section",
  "everyday-essentials": "Everyday essentials section",
  "shop-by-brand": "Shop by brand section",
  "electrical-essentials": "Electrical essentials section",
  "hardware-tools": "Hardware and tools section",
  "plumbing-bathroom": "Plumbing and bathroom section",
  "recently-added": "Recently added section",
  "best-selling": "Best-selling section",
};

const AdminHomeSectionsPage: React.FC = () => {
  const [sections, setSections] = useState<HomepageSectionContent[]>([]);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newSection, setNewSection] = useState({
    sectionKey: "",
    heading: "",
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadSections = async () => {
      try {
        const response = await fetchAdminHomepageSections();
        if (isMounted) {
          setSections(response);
        }
      } catch {
        toast.error("Unable to load homepage sections.");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    void loadSections();

    return () => {
      isMounted = false;
    };
  }, []);

  const updateSectionField = (
    sectionKey: string,
    field: "heading",
    value: string,
  ) => {
    setSections((current) =>
      current.map((section) =>
        section.sectionKey === sectionKey
          ? {
              ...section,
              [field]: value,
            }
          : section,
      ),
    );
  };

  const saveSection = async (section: HomepageSectionContent) => {
    if (!section.heading.trim()) {
      toast.error("Heading is required.");
      return;
    }

    setSavingKey(section.sectionKey);
    try {
      const updatedSection = await updateAdminHomepageSection(section.sectionKey, {
        heading: section.heading.trim(),
      });
      setSections((current) =>
        current.map((item) =>
          item.sectionKey === updatedSection.sectionKey ? updatedSection : item,
        ),
      );
      toast.success("Homepage section updated.");
    } catch {
      toast.error("Unable to update homepage section.");
    } finally {
      setSavingKey(null);
    }
  };

  const deleteSection = async (sectionKey: string) => {
    const normalizedSectionKey = sectionKey.trim();
    if (!normalizedSectionKey) {
      toast.error("Section key is required.");
      return;
    }

    setDeletingKey(normalizedSectionKey);
    try {
      await deleteAdminHomepageSection(normalizedSectionKey);
      setSections((current) => current.filter((section) => section.sectionKey !== normalizedSectionKey));
      toast.success("Homepage section deleted.");
    } catch {
      toast.error("Unable to delete homepage section.");
    } finally {
      setDeletingKey(null);
    }
  };

  const createSection = async () => {
    if (!newSection.sectionKey.trim() || !newSection.heading.trim()) {
      toast.error("Section key and heading are required.");
      return;
    }

    setCreating(true);
    try {
      const createdSection = await createAdminHomepageSection({
        sectionKey: newSection.sectionKey.trim(),
        heading: newSection.heading.trim(),
      });
      setSections((current) => [...current, createdSection]);
      setNewSection({ sectionKey: "", heading: "" });
      toast.success("Homepage section added.");
    } catch {
      toast.error("Unable to add homepage section.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <AdminWorkspaceNav />
      <section className="shell section page-section">
        <div className="admin-page-heading">
          <div>
            <span className="eyebrow">Admin workspace</span>
            <h1>Homepage section copy</h1>
          </div>
        </div>

        <section className="store-card admin-panel admin-panel--full">
          <div className="admin-panel__heading">
            <div>
              <span className="eyebrow">Section text</span>
              <h2>Edit section headings</h2>
            </div>
          </div>

          <form
            className="admin-home-section-editor__create"
            onSubmit={(event) => {
              event.preventDefault();
              void createSection();
            }}
          >
            <strong>Add homepage section</strong>
            <div className="admin-home-section-editor__create-grid">
              <label>
                Section key
                <input
                  value={newSection.sectionKey}
                  onChange={(event) =>
                    setNewSection((current) => ({ ...current, sectionKey: event.target.value }))
                  }
                  placeholder="extra-offers"
                />
              </label>
              <label>
                Section heading
                <input
                  value={newSection.heading}
                  onChange={(event) =>
                    setNewSection((current) => ({ ...current, heading: event.target.value }))
                  }
                  placeholder="Extra Offers"
                />
              </label>
            </div>
            <button className="button" type="submit" disabled={creating}>
              {creating ? "Adding..." : "Add section"}
            </button>
          </form>

          {loading ? (
            <p className="admin-empty-state">Loading homepage sections...</p>
          ) : (
            <div className="admin-home-section-editor">
              {sections.map((section) => (
                <form
                  key={section.sectionKey}
                  className="admin-home-section-editor__item"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void saveSection(section);
                  }}
                >
                  <div className="admin-home-section-editor__title">
                    <strong>{sectionLabels[section.sectionKey] || section.sectionKey}</strong>
                    <span>{section.sectionKey}</span>
                  </div>
                  <label>
                    Section heading
                    <input
                      maxLength={120}
                      value={section.heading}
                      onChange={(event) =>
                        updateSectionField(section.sectionKey, "heading", event.target.value)
                      }
                    />
                  </label>
                  <button
                    className="button"
                    type="submit"
                    disabled={savingKey === section.sectionKey}
                  >
                    {savingKey === section.sectionKey ? "Saving..." : "Save"}
                  </button>
                  <button
                    className="link-button admin-danger-button"
                    type="button"
                    disabled={deletingKey === section.sectionKey}
                    onClick={() => {
                      if (window.confirm(`Delete homepage section "${section.sectionKey}"?`)) {
                        void deleteSection(section.sectionKey);
                      }
                    }}
                  >
                    {deletingKey === section.sectionKey ? "Deleting..." : "Delete"}
                  </button>
                </form>
              ))}
            </div>
          )}
        </section>
      </section>
    </>
  );
};

export default AdminHomeSectionsPage;
